import { useCallback, useContext, useEffect, useState } from "react";
import { ResourceWrapper } from "../../../../../eventer-pod/client/ResourceWrapper.ts";
import Session from "../../../../../appfw/authentication/Session.ts";
import { SessionContext } from "../../../../../appfw/authentication/SessionProvider.tsx"
import { FetchPodEventsRequest } from "@eventer/api-client";
import Semantic from "../../../../../eventer/Semantic.ts";
import { useLoadingControl } from "../../../../../appfw/loading/use-loading-control.tsx";
import { useEventerConsolePod } from "../../../hooks/use-eventer-console-pod.ts";

class EventsFeed {

    startDate: Date
    lastSyncDate: Date = new Date()

    startDateMin: Date | undefined = undefined
    startDateMax: Date | undefined = undefined

    firstEventUri: undefined | string | null = undefined
    events: ResourceWrapper[] = []

    prevUrl: undefined | string | null = undefined
    nextUrl: undefined | string | null = undefined

    loadingIdSuffix = "main"

    constructor() {
        const now = new Date()
        this.startDate = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate(),
            0, 0, 0, 0
        )
    }

    putEvents(newEvents: ResourceWrapper[]): boolean {
        if (!newEvents) {
            return false;
        }

        let changed = false;

        const map = new Map<string, ResourceWrapper>();
        for (const event of [...this.events, ...newEvents]) {
            const existing = map.get(event.subject);
            if (!existing || !existing.equals(event)) {
                map.set(event.subject, event)
                changed = true;
            }
        }

        const result = Array.from(map.values()).sort((a, b) => {
            const aTime = a.getMinDate(Semantic.SchemaOrg.Predicates.startDate.uri)?.getTime() ?? 0;
            const bTime = b.getMinDate(Semantic.SchemaOrg.Predicates.startDate.uri)?.getTime() ?? 0;
            return aTime - bTime;
        })

        if (changed) {
            this.events = result;
        }

        if (this.firstEventUri === undefined) {
            if (this.events.length === 0) {
                this.firstEventUri = null
            } else {
                this.firstEventUri = this.events[0].subject
            }
        }

        if (this.events.length > 1) {
            this.startDateMin = this.events[0].getMinDate(Semantic.SchemaOrg.Predicates.startDate.uri)
            this.startDateMax = this.events[this.events.length - 1]
                .getMinDate(Semantic.SchemaOrg.Predicates.startDate.uri)
        }

        return changed
    }
}

// TODO: later "zustand" to store global states?
const eventFeedsCache = new Map<string, EventsFeed>()

function getEventsFeed(pod: string): EventsFeed {
    let entry = eventFeedsCache.get(pod)
    if (entry == undefined) {
        entry = new EventsFeed()
        eventFeedsCache.set(pod, entry)
    }
    return entry
}


export interface useEventFeedsProps {
    pod: string,
}

export function useEventsFeed(props: useEventFeedsProps) {

    const session = useContext<undefined | null | Session>(SessionContext)

    const pod = useEventerConsolePod()

    const feed = getEventsFeed(props.pod)

    // TODO: events should not be part of this "feed info", only the "frame meta data"
    //   => events should be fetched on demand from the local pod
    const [events, setEvents] = useState<ResourceWrapper[]>(feed.events)

    const [hasPrev, setHasPrev] = useState(feed.prevUrl !== null)
    const [hasNext, setHasNext] = useState(feed.nextUrl !== null)

    const loadingControl = useLoadingControl("events-feed-" + props.pod)

    const isLoading = loadingControl.isLoading

    const isLoadingPrevId = `loading-prev-${feed.loadingIdSuffix}`
    const isLoadingNextId = `loading-next-${feed.loadingIdSuffix}`
    const isLoadingRefreshId = `loading-refresh-${feed.loadingIdSuffix}`
    const isLoadingPrev = () => loadingControl.isLoadingId(isLoadingPrevId)
    const isLoadingNext = () => loadingControl.isLoadingId(isLoadingNextId)

    useEffect(() => {

        if (!pod || !session?.id) {
            return
        }

        if (feed.nextUrl === undefined) {
            loadNext()
        }
    }, [pod, session?.id])

    const loadPrev = useCallback(async () => {

        if (!session || !pod) {
            return
        }

        if (feed.prevUrl === null) {
            // already reached end of the feed
            setHasNext(false)
            return
        }

        loadingControl.put(isLoadingPrevId)

        try {

            console.log("feed: loadPrev", feed.prevUrl)

            let result
            if (feed.prevUrl === undefined) {
                const request: FetchPodEventsRequest = {
                    pod: props.pod,
                    startDateMax: feed.startDate,
                    limit: 10,
                    sort: 'startDate_desc',
                    expand: ["image"],
                }
                result = await pod.loadPodEvents({ request: request, sessionId: session.id })
            } else {
                result = await pod.loadNextPodEvents({ url: feed.prevUrl, sessionId: session.id })
            }

            if (result.data) {
                if (feed.putEvents(result.data.events)) {
                    setEvents(feed.events)
                }
                feed.prevUrl = result.data.nextUrl
                setHasPrev(result.data.nextUrl != null)
            }

        } finally {
            loadingControl.remove(isLoadingPrevId)
        }

    }, [pod, session?.id, loadingControl])

    const loadNext = useCallback(async () => {

        if (!session || !pod) {
            return
        }

        if (feed.nextUrl === null) {
            // already reached end of the feed
            setHasNext(false)
            return
        }

        loadingControl.put(isLoadingNextId)

        try {

            console.log("feed: loadNext", feed.nextUrl)

            let result
            if (feed.nextUrl === undefined) {
                const request: FetchPodEventsRequest = {
                    pod: props.pod,
                    startDateMin: feed.startDate,
                    limit: 10,
                    sort: 'startDate_asc',
                    expand: ["image"],
                }
                result = await pod.loadPodEvents({ request: request, sessionId: session.id })
            } else {
                result = await pod.loadNextPodEvents({ url: feed.nextUrl, sessionId: session.id })
            }

            if (result.data) {
                if (feed.putEvents(result.data.events)) {
                    setEvents(feed.events)
                }
                feed.nextUrl = result.data.nextUrl
                setHasNext(result.data.nextUrl != null)
            }

        } finally {
            loadingControl.remove(isLoadingNextId)
        }

    }, [pod, session?.id, loadingControl])

    const refresh = useCallback(async () => {

        console.log("feed: refresh called", session?.id, pod)

        if (!session || !pod) {
            return
        }

        console.log("feed: refresh called - 2")

        if (loadingControl.isLoadingId(isLoadingRefreshId)) {
            // already refreshing
            return
        }

        loadingControl.put(isLoadingRefreshId)

        try {

            const now = new Date()

            console.log("feed: refresh called - 3")

            let hasMore = true
            let nextUrl = undefined
            while (hasMore) {
                let result
                if (nextUrl === undefined) {
                    const request: FetchPodEventsRequest = {
                        pod: props.pod,
                        startDateMin: feed.startDateMin,
                        startDateMax: feed.startDateMax,
                        dateModifiedMin: feed.lastSyncDate,
                        sort: 'dateModified_asc',
                    }
                    result = await pod.loadPodEvents({ request: request, sessionId: session.id })
                } else {
                    result = await pod.loadNextPodEvents({ url: nextUrl, sessionId: session.id })
                }
                if (result.data) {
                    feed.putEvents(result.data.events)

                    console.log("refresh: called ==> response", result)

                    nextUrl = result.data.nextUrl
                    hasMore = nextUrl != null && result.data.events.length > 0
                } else {
                    hasMore = false
                }
            }

            // Could be more clever ...?
            feed.lastSyncDate = now

        } finally {
            loadingControl.remove(isLoadingRefreshId)
        }
    }, [pod, session?.id, loadingControl])

    const reset = useCallback(() => {
        console.log("feed: reset called", session?.id, pod)
        eventFeedsCache.delete(props.pod)
    }, [])

    return {
        pod,
        events,
        hasPrev,
        hasNext,
        loadPrev,
        loadNext,
        isLoading,
        isLoadingPrev,
        isLoadingNext,
        refresh,
        reset,
    }
}